import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { bool, exact, string, arrayOf, number } from 'prop-types';
import { Main } from '@templates';
import { Typography, Flex } from '@mixins';
import { theme } from '@styles';
import { getCompanyMembersByAccessId, resetPhases } from '@store/company/duck';
import { useDebounce } from '@hooks';
import { ManageCompanyMemberModal, TeamMembersManagement } from '@organisms';

import { ReactComponent as Dropdown } from '@assets/svgs/Dropdown.svg';
import { ManageCompanyMembersErrorModal } from '@molecules';
import { BackButton } from '../EditUserProfile/styles';

const CompanyMembersApp = ({ companyMembers, pagination, companyName, companyStatus, activeMembers }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { updateCompanyMemberError } = useSelector(store => store.companyStore);

  const [sortingModel, setSortingModel] = useState({
    direction: null,
    sortBy: null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    dispatch(
      getCompanyMembersByAccessId('my', {
        page,
        search: debouncedSearch,
        pageSize: 8,
        ...sortingModel,
      }),
    );
  }, [page, debouncedSearch, sortingModel]);

  const handleAddUser = () => {
    setIsModalOpen(true);
  };

  const handleResetState = () => {
    dispatch(resetPhases());
  };

  const handlePageChange = useCallback(value => {
    setPage(value.page + 1);
  }, []);

  const onBackClick = () => {
    history.goBack();
  };

  const handleUserClick = id => {
    setIsModalOpen(true);
    setSelectedUserId(id);
  };

  const handleSortModelChange = sorting => {
    setSortingModel({
      orderBy: sorting[0]?.field,
      direction: sorting[0]?.sort.toUpperCase(),
    });
  };

  const handleSearch = event => {
    setSearch(event.target.value);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUserId(null);
  };

  const selectedUser = useMemo(
    () => companyMembers.find(user => user.id === selectedUserId),
    [companyMembers, selectedUserId],
  );

  return (
    <Main isWhiteLabelingOpen={false} isLoading={false}>
      <Flex alignItems="start" width="100%" flexDirection="column">
        <Flex width="fit-content" alignItems="center" mb={[20, 25, 30]} onClick={onBackClick} pointer>
          <BackButton mr={['9px', 11, 13]} SVG={Dropdown} color={theme.color.gray[500]} />
          <Typography variant="diatype-400">Back</Typography>
        </Flex>
        <Typography
          mb="30px"
          color={theme.color.black}
          variant="diatype-500"
          fontSize={['18px', '24px', '30px', '36px']}>
          Manage team members
        </Typography>
        <Flex
          flexDirection="column"
          width="100%"
          border="1px solid #D9D9D9"
          py={['20px', '20px', '30px', '60px']}
          px={['20px', '20px', '30px', '60px']}>
          <TeamMembersManagement
            page={page}
            onPageChange={handlePageChange}
            search={search}
            onSearch={handleSearch}
            companyMembers={companyMembers}
            pagination={pagination}
            companyName={companyName}
            companyStatus={companyStatus}
            activeMembers={activeMembers}
            onAddUser={handleAddUser}
            onUserClick={handleUserClick}
            onSortModelChange={handleSortModelChange}
          />
        </Flex>
      </Flex>
      {isModalOpen && (
        <ManageCompanyMemberModal user={selectedUser} onModalClose={handleModalClose} isOpen={isModalOpen} />
      )}
      {!!updateCompanyMemberError && !isModalOpen && (
        <ManageCompanyMembersErrorModal
          error={{
            title: 'Cannot edit team member',
            status: updateCompanyMemberError?.data.error.status,
            message: updateCompanyMemberError?.data.error.message,
          }}
          onModalClose={handleResetState}
          isOpen={!!updateCompanyMemberError}
        />
      )}
    </Main>
  );
};

CompanyMembersApp.propTypes = {
  activeMembers: number,
  companyStatus: string.isRequired,
  companyName: string,
  companyMembers: arrayOf(
    exact({
      id: number,
      imageUrl: string,
      email: string,
      firstName: string,
      lastName: string,
      isCompanyAdmin: bool,
      phone: string,
      status: string,
      isAbleToBookMeetingRoom: bool,
      isAbleToInvoice: bool,
      isAbleToPayByCard: bool,
    }),
  ).isRequired,
  pagination: exact({
    pageSize: number,
    page: number,
    cursor: number,
    total: number,
  }).isRequired,
};

CompanyMembersApp.defaultProps = {
  activeMembers: 0,
  companyName: null,
};

export default CompanyMembersApp;
